import { useState, useEffect, useRef, ChangeEvent } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePartnerCatalogue, type PartnerProduct, type PartnerExtra } from '@/hooks/usePartnerCatalogue';
import { Plus, ChevronDown, ChevronUp, Upload, X } from 'lucide-react';

// ── Image compression ─────────────────────────────────────────────────────────

function compressImage(file: File, maxPx = 1200, quality = 0.82): Promise<File> {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(file); return; }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        blob => {
          if (!blob) { resolve(file); return; }
          const name = file.name.replace(/\.[^.]+$/, '.jpg');
          resolve(new File([blob], name, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        quality,
      );
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });
}

// ── Toggle ────────────────────────────────────────────────────────────────────

function Toggle({ active, onToggle }: { active: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className="relative w-9 h-5 rounded-full transition-colors duration-200 shrink-0"
      style={{ background: active ? 'var(--accent)' : 'var(--border-primary)' }}
    >
      <span
        className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-200"
        style={{ left: active ? 'calc(100% - 18px)' : '2px' }}
      />
    </button>
  );
}

// ── TagChip ───────────────────────────────────────────────────────────────────

function TagChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: '3px 8px', background: 'var(--bg-input)',
      border: '1px solid var(--border-separator)', borderRadius: '20px',
      fontSize: '11px', color: 'var(--text-secondary)',
    }}>
      {label}
      <button
        onClick={onRemove}
        style={{ display: 'flex', alignItems: 'center', color: 'var(--text-muted)',
          background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        <X size={10} />
      </button>
    </span>
  );
}

// ── TagInput ──────────────────────────────────────────────────────────────────

function TagInput({ onAdd, placeholder }: { onAdd: (v: string) => void; placeholder: string }) {
  const [value, setValue] = useState('');
  return (
    <input
      type="text"
      value={value}
      onChange={e => setValue(e.target.value)}
      onKeyDown={e => {
        if ((e.key === 'Enter' || e.key === ',') && value.trim()) {
          e.preventDefault();
          onAdd(value.trim());
          setValue('');
        }
      }}
      onBlur={() => {
        if (value.trim()) { onAdd(value.trim()); setValue(''); }
      }}
      placeholder={placeholder}
      style={{
        fontSize: '11px', padding: '3px 8px', borderRadius: '20px',
        border: '1px dashed var(--border-primary)', background: 'transparent',
        color: 'var(--text-secondary)', outline: 'none', width: '140px',
      }}
    />
  );
}

// ── ProductCard ───────────────────────────────────────────────────────────────

function ProductCard({
  product,
  isExpanded,
  onToggleExpand,
  onChange,
  onCommit,
  onToggleActive,
  onDelete,
  onImageUpload,
  focusRef,
  isFocusTarget,
}: {
  product: PartnerProduct;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onChange: (field: keyof PartnerProduct, value: unknown) => void;
  onCommit: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
  onImageUpload: (file: File) => Promise<void>;
  focusRef: React.RefObject<HTMLInputElement>;
  isFocusTarget: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    await onImageUpload(file);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div style={{
      border: `1px solid ${isExpanded ? 'var(--accent)' : 'var(--border-separator)'}`,
      borderRadius: '12px', overflow: 'hidden',
      background: 'var(--bg-surface-raised)',
      transition: 'border-color 0.2s',
    }}>
      {/* Collapsed header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px' }}>

        {/* Thumbnail */}
        <div
          onClick={() => { if (!uploading) fileInputRef.current?.click(); }}
          style={{
            width: '40px', height: '40px', borderRadius: '8px', flexShrink: 0,
            overflow: 'hidden', background: 'var(--bg-input)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid var(--border-separator)',
            cursor: uploading ? 'wait' : 'pointer',
            pointerEvents: uploading ? 'none' : 'auto',
          }}
        >
          {uploading ? (
            <div style={{ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid var(--border-primary)', borderTopColor: 'var(--accent)', animation: 'spin 0.8s linear infinite' }} />
          ) : product.image_url ? (
            <img src={product.image_url} alt={product.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Upload size={14} style={{ color: 'var(--text-muted)' }} />
          )}
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" disabled={uploading}
          style={{ display: 'none' }} onChange={handleFileChange} />

        {/* Name */}
        <input
          ref={isFocusTarget ? focusRef : null}
          type="text"
          value={product.name}
          onChange={e => onChange('name', e.target.value)}
          onBlur={onCommit}
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)',
          }}
        />

        {/* Price */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>€</span>
          <input
            type="number" min="0" step="0.01"
            value={product.price_per_m2 === 0 ? '' : product.price_per_m2}
            placeholder="0"
            onChange={e => onChange('price_per_m2', parseFloat(e.target.value) || 0)}
            onBlur={onCommit}
            style={{
              width: '64px', textAlign: 'right', background: 'transparent',
              border: 'none', outline: 'none', fontSize: '14px',
              fontWeight: 500, color: 'var(--text-primary)',
            }}
          />
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>/m²</span>
        </div>

        <Toggle active={product.active} onToggle={onToggleActive} />

        <button
          onClick={onToggleExpand}
          style={{
            padding: '4px', color: 'var(--text-muted)', background: 'none',
            border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
          }}
        >
          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        <button
          onClick={onDelete}
          style={{
            padding: '4px', color: 'var(--danger-text)', background: 'none',
            border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
            opacity: 0.45,
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.45'; }}
        >
          <i className="ti ti-trash" style={{ fontSize: '13px' }} />
        </button>
      </div>

      {/* Expanded editor */}
      {isExpanded && (
        <div style={{
          borderTop: '1px solid var(--border-separator)',
          padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '16px', alignItems: 'flex-start' }}>

            {/* Image upload */}
            <div>
              <div style={{
                fontSize: '11px', fontWeight: 500, textTransform: 'uppercase',
                letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: '8px',
              }}>
                Productfoto
              </div>
              <div
                onClick={() => { if (!uploading) fileInputRef.current?.click(); }}
                style={{
                  width: '140px', height: '100px', borderRadius: '8px',
                  overflow: 'hidden', background: 'var(--bg-input)',
                  cursor: uploading ? 'wait' : 'pointer',
                  pointerEvents: uploading ? 'none' : 'auto',
                  border: `2px dashed ${product.image_url ? 'transparent' : 'var(--border-primary)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexDirection: 'column', gap: '6px', position: 'relative',
                }}
              >
                {uploading ? (
                  <>
                    {product.image_url && (
                      <img src={product.image_url} alt={product.name}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.4 }} />
                    )}
                    <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'var(--accent)', animation: 'spin 0.8s linear infinite' }} />
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Uploaden…</span>
                    </div>
                  </>
                ) : product.image_url ? (
                  <>
                    <img src={product.image_url} alt={product.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div
                      style={{
                        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: 0, transition: 'opacity 0.2s',
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.opacity = '1'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.opacity = '0'; }}
                    >
                      <Upload size={20} style={{ color: '#fff' }} />
                    </div>
                  </>
                ) : (
                  <>
                    <Upload size={20} style={{ color: 'var(--text-muted)' }} />
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      Foto uploaden
                    </span>
                  </>
                )}
              </div>
              {product.image_url && (
                <button
                  onClick={() => { onChange('image_url', null); onCommit(); }}
                  style={{
                    marginTop: '6px', fontSize: '11px', color: 'var(--danger-text)',
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  }}
                >
                  Foto verwijderen
                </button>
              )}
            </div>

            {/* Description */}
            <div>
              <div style={{
                fontSize: '11px', fontWeight: 500, textTransform: 'uppercase',
                letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: '8px',
              }}>
                Omschrijving <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: '11px', opacity: 0.7 }}>voor brochure</span>
              </div>
              <textarea
                value={product.description}
                onChange={e => onChange('description', e.target.value)}
                onBlur={onCommit}
                placeholder="Beschrijf het product in 1-3 zinnen. Wordt gebruikt in de brochure die naar leads wordt gestuurd."
                rows={4}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: '1px solid var(--border-primary)', background: 'var(--bg-input)',
                  color: 'var(--text-primary)', fontSize: '13px', resize: 'none',
                  outline: 'none', lineHeight: 1.6,
                }}
              />
            </div>
          </div>

          {/* Specs */}
          <div>
            <div style={{
              fontSize: '11px', fontWeight: 500, textTransform: 'uppercase',
              letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: '8px',
            }}>
              Specificaties
              <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: '8px', fontSize: '11px', opacity: 0.7 }}>
                bijv. Waterdicht · Vloerverwarming geschikt · Click systeem
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
              {product.specs.map((spec, i) => (
                <TagChip key={i} label={spec} onRemove={() => {
                  const updated = product.specs.filter((_, idx) => idx !== i);
                  onChange('specs', updated);
                  onCommit();
                }} />
              ))}
              <TagInput
                placeholder="+ Toevoegen (Enter)"
                onAdd={v => {
                  const updated = [...product.specs, v];
                  onChange('specs', updated);
                  onCommit();
                }}
              />
            </div>
          </div>

          {/* USPs */}
          <div>
            <div style={{
              fontSize: '11px', fontWeight: 500, textTransform: 'uppercase',
              letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: '8px',
            }}>
              Voordelen (USPs)
              <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: '8px', fontSize: '11px', opacity: 0.7 }}>
                bijv. All-in prijs · 10 jaar garantie · Gratis inmeting
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
              {product.usps.map((usp, i) => (
                <TagChip key={i} label={usp} onRemove={() => {
                  const updated = product.usps.filter((_, idx) => idx !== i);
                  onChange('usps', updated);
                  onCommit();
                }} />
              ))}
              <TagInput
                placeholder="+ Toevoegen (Enter)"
                onAdd={v => {
                  const updated = [...product.usps, v];
                  onChange('usps', updated);
                  onCommit();
                }}
              />
            </div>
          </div>

          {/* Brochure hint */}
          <div style={{
            padding: '10px 12px',
            background: 'rgba(var(--accent-rgb, 59,130,246), 0.06)',
            borderRadius: '8px', border: '1px solid var(--accent)',
            fontSize: '12px', color: 'var(--accent)',
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <i className="ti ti-file-text" style={{ fontSize: '14px', flexShrink: 0 }} />
            De foto, omschrijving, specs en voordelen worden automatisch gebruikt in de brochure die naar leads wordt gestuurd.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PartnerCataloguePage() {
  const { effectivePartnerId } = useAuth();
  const {
    products, extras, loading,
    saveProduct, saveExtra,
    addProduct, addExtra,
    deleteProduct, deleteExtra,
    uploadProductImage,
  } = usePartnerCatalogue(effectivePartnerId);

  const [localProducts, setLocalProducts] = useState<PartnerProduct[]>([]);
  const [localExtras, setLocalExtras]     = useState<PartnerExtra[]>([]);
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const focusRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setLocalProducts(products); }, [products]);
  useEffect(() => { setLocalExtras(extras); }, [extras]);

  useEffect(() => {
    if (focusId && focusRef.current) {
      focusRef.current.focus();
      focusRef.current.select();
      setFocusId(null);
    }
  }, [focusId, localProducts, localExtras]);

  const updateProduct = (id: string, field: keyof PartnerProduct, value: unknown) =>
    setLocalProducts(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));

  const commitProduct = (id: string) => {
    const p = localProducts.find(x => x.id === id);
    if (p) saveProduct(p);
  };

  const handleImageUpload = async (productId: string, file: File): Promise<void> => {
    if (!effectivePartnerId) return;
    const compressed = await compressImage(file);
    const url = await uploadProductImage(productId, effectivePartnerId, compressed);
    if (url) {
      updateProduct(productId, 'image_url', url);
      const updated = localProducts.find(p => p.id === productId);
      if (updated) saveProduct({ ...updated, image_url: url });
    }
  };

  const handleAddProduct = async () => {
    if (!effectivePartnerId) return;
    const id = await addProduct(effectivePartnerId);
    if (id) {
      setFocusId(id);
      setExpandedProduct(id);
    }
  };

  const updateExtra = (id: string, field: keyof PartnerExtra, value: unknown) =>
    setLocalExtras(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));

  const commitExtra = (id: string) => {
    const e = localExtras.find(x => x.id === id);
    if (e) saveExtra(e);
  };

  const toggleExtra = (id: string) => {
    const e = localExtras.find(x => x.id === id);
    if (!e) return;
    const updated = { ...e, active: !e.active };
    setLocalExtras(prev => prev.map(x => x.id === id ? updated : x));
    saveExtra(updated);
  };

  if (loading) {
    return (
      <div className="space-y-4 max-w-3xl">
        {[1, 2, 3].map(i => (
          <div key={i} className="rounded-xl border animate-pulse"
            style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-separator)', height: '68px' }} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-3xl animate-fade-up">

      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
          Productcatalogus
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
          Prijzen, foto's en beschrijvingen voor offertes en brochures.
        </p>
      </div>

      {/* ── Products ── */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '11px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)' }}>
              Producten
            </span>
            <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '20px', background: 'var(--bg-input)', color: 'var(--text-muted)' }}>
              {localProducts.length}
            </span>
          </div>
          <button
            onClick={handleAddProduct}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 500,
              background: 'var(--bg-input)', color: 'var(--text-secondary)',
              border: '1px solid var(--border-primary)', cursor: 'pointer',
            }}
          >
            <Plus size={12} /> Product toevoegen
          </button>
        </div>

        {localProducts.length === 0 ? (
          <div style={{
            border: '1px dashed var(--border-primary)', borderRadius: '12px',
            padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px',
          }}>
            Geen producten — klik op "Product toevoegen"
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {localProducts.map(product => (
              <ProductCard
                key={product.id}
                product={product}
                isExpanded={expandedProduct === product.id}
                onToggleExpand={() =>
                  setExpandedProduct(prev => prev === product.id ? null : product.id)}
                onChange={(field, value) => updateProduct(product.id, field, value)}
                onCommit={() => commitProduct(product.id)}
                onToggleActive={() => {
                  const updated = { ...product, active: !product.active };
                  setLocalProducts(prev => prev.map(p => p.id === product.id ? updated : p));
                  saveProduct(updated);
                }}
                onDelete={() => {
                  setLocalProducts(prev => prev.filter(p => p.id !== product.id));
                  deleteProduct(product.id);
                  if (expandedProduct === product.id) setExpandedProduct(null);
                }}
                onImageUpload={file => handleImageUpload(product.id, file)}
                focusRef={focusRef}
                isFocusTarget={focusId === product.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Extras ── */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '11px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)' }}>
              Extra's
            </span>
            <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '20px', background: 'var(--bg-input)', color: 'var(--text-muted)' }}>
              {localExtras.length}
            </span>
          </div>
          <button
            onClick={async () => {
              if (!effectivePartnerId) return;
              const id = await addExtra(effectivePartnerId);
              if (id) setFocusId(id);
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 500,
              background: 'var(--bg-input)', color: 'var(--text-secondary)',
              border: '1px solid var(--border-primary)', cursor: 'pointer',
            }}
          >
            <Plus size={12} /> Extra toevoegen
          </button>
        </div>

        <div style={{ border: '1px solid var(--border-separator)', borderRadius: '12px', overflow: 'hidden', background: 'var(--bg-surface-raised)' }}>
          {/* Column headers */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 88px 56px 32px', gap: '12px', padding: '8px 16px', borderBottom: '1px solid var(--border-separator)' }}>
            {(['Naam', 'Prijs', 'Eenheid', 'Actief', ''] as const).map(h => (
              <span key={h} style={{
                fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.07em', color: 'var(--text-muted)',
                textAlign: h === 'Prijs' ? 'right' : h === 'Naam' ? 'left' : 'center',
              }}>
                {h}
              </span>
            ))}
          </div>

          {localExtras.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', fontSize: '13px', color: 'var(--text-muted)' }}>
              Geen extra's — klik op "Extra toevoegen"
            </div>
          ) : (
            localExtras.map((extra, idx) => (
              <div
                key={extra.id}
                className="group"
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 100px 88px 56px 32px',
                  gap: '12px', padding: '10px 16px', alignItems: 'center',
                  borderTop: idx === 0 ? 'none' : '1px solid var(--border-separator)',
                }}
              >
                <input
                  ref={focusId === extra.id ? focusRef : null}
                  type="text" value={extra.name}
                  onChange={e => updateExtra(extra.id, 'name', e.target.value)}
                  onBlur={() => commitExtra(extra.id)}
                  style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: '13px', color: 'var(--text-primary)' }}
                />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>€</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={extra.price === 0 ? '' : extra.price}
                    placeholder="0"
                    onChange={e => updateExtra(extra.id, 'price', parseFloat(e.target.value) || 0)}
                    onBlur={() => commitExtra(extra.id)}
                    style={{ width: '64px', textAlign: 'right', background: 'transparent', border: 'none', outline: 'none', fontSize: '13px', color: 'var(--text-primary)' }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <select
                    value={extra.unit}
                    onChange={e => {
                      const updated = { ...extra, unit: e.target.value as 'm2' | 'vast' };
                      setLocalExtras(prev => prev.map(x => x.id === extra.id ? updated : x));
                      saveExtra(updated);
                    }}
                    style={{
                      fontSize: '12px', padding: '3px 8px', borderRadius: '6px',
                      background: 'var(--bg-input)', color: 'var(--text-secondary)',
                      border: '1px solid var(--border-primary)', cursor: 'pointer', outline: 'none',
                    }}
                  >
                    <option value="vast">vast</option>
                    <option value="m2">m²</option>
                  </select>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Toggle active={extra.active} onToggle={() => toggleExtra(extra.id)} />
                </div>
                <button
                  onClick={() => {
                    setLocalExtras(prev => prev.filter(e => e.id !== extra.id));
                    deleteExtra(extra.id);
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--danger-text)', background: 'none', border: 'none',
                    cursor: 'pointer', opacity: 0, transition: 'opacity 0.15s',
                  }}
                  className="group-hover:opacity-100"
                >
                  <i className="ti ti-trash" style={{ fontSize: '13px' }} />
                </button>
              </div>
            ))
          )}

          {localExtras.length > 0 && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border-separator)', background: 'var(--bg-input)', fontSize: '11px', color: 'var(--text-muted)' }}>
              <strong>m²</strong> wordt vermenigvuldigd met de oppervlakte · <strong>vast</strong> is een vaste toeslag per offerte
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
